// ============================================================
// LOCATION SECTION - Type Definitions
// Frontend-only content management (no backend API)
// ============================================================

import { SectionContentProps } from "../../types";

// --- Layout Variants ---

export type LocationVariant = "full-width" | "split" | "sidebar" | "multi";

// --- Business Hours Interface ---

export interface BusinessHours {
  day: string;
  open: string;
  close: string;
  isClosed?: boolean;
}

// --- Location Content (Main Section Content) ---

export interface LocationContent {
  // Layout & variant
  variant?: LocationVariant;

  // Title & description
  title?: string;
  subtitle?: string;
  description?: string;

  // Address fields (auto-convert to embed URL)
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;

  // Map embed URL (generated from address or manual)
  mapEmbedUrl?: string;
  mapProvider?: "google" | "openstreetmap" | "bing";

  // Contact information
  phone?: string;
  email?: string;
  website?: string;

  // Business hours
  hours?: BusinessHours[];

  // Display options
  showGetDirections?: boolean;
  showPhone?: boolean;
  showEmail?: boolean;
  showHours?: boolean;
  showAddress?: boolean;

  // CTA
  ctaText?: string;
  ctaLink?: string;

  // Multi-location support
  locations?: LocationContent[];

  // Font
  fontFamily?: string;

  // Styling
  primaryColor?: string;
  backgroundColor?: string;
  textColor?: string;

  // SEO
  seoTitle?: string;
  seoDescription?: string;

  // Custom
  isPrimary?: boolean;
}

// --- Component Props ---

export interface LocationSectionProps extends SectionContentProps {
  content: LocationContent;
}

// --- Helper: Build full address string ---

export function buildFullAddress(content: LocationContent): string {
  const parts = [
    content.address,
    content.city,
    content.state,
    content.postalCode,
    content.country,
  ].filter(Boolean);
  return parts.join(", ");
}

// --- Helper: Generate embed URL from address ---

export function generateMapEmbedUrl(
  address: string,
  provider: "google" | "openstreetmap" | "bing" = "google"
): string {
  if (!address) return "";

  const encodedAddress = encodeURIComponent(address);

  switch (provider) {
    case "openstreetmap":
      return `https://www.openstreetmap.org/export/embed.html?bbox=&layer=mapnik&marker=`;
    case "bing":
      return `https://www.bing.com/maps/embed?h=400&w=600&cp=${encodedAddress}`;
    case "google":
    default:
      return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5m2!1sen!2sus!5m1!1sen!6m3!1s${encodedAddress}`;
  }
}

// --- Default Content ---

export const getDefaultLocationContent = (
  variant: LocationVariant = "full-width"
): LocationContent => {
  const baseContent: LocationContent = {
    variant,
    title: "Visit Our Location",
    subtitle: "Find us at our headquarters",
    showGetDirections: true,
    showPhone: true,
    showEmail: false,
    showHours: false,
    showAddress: true,
    mapProvider: "google",
    ctaText: "Get Directions",
  };

  switch (variant) {
    case "full-width":
      return {
        ...baseContent,
        title: "Find Us",
        subtitle: "Visit our main campus",
        description: "We're conveniently located in the heart of downtown.",
      };

    case "split":
      return {
        ...baseContent,
        title: "Our Location",
        subtitle: "Get directions to our office",
        description: "Easy access from major highways and public transportation.",
      };

    case "sidebar":
      return {
        ...baseContent,
        title: "Contact & Location",
        subtitle: "Reach out or visit us",
        showPhone: true,
        showEmail: true,
        showHours: true,
        ctaText: "Send Message",
      };

    case "multi":
      return {
        ...baseContent,
        title: "Our Locations",
        subtitle: "Multiple convenient locations to serve you",
        locations: [],
      };

    default:
      return baseContent;
  }
};
